import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Router } from '@angular/router';
import { CostInfo } from '@models/session-details';
import { PriceData, ShoppingCartItem } from '@models/shopping-cart.model';
import { ShoppingCartData } from '@models/shopping-cart.model';
import { CART_ITEM_TYPE_DEVICE, DeviceItemDevice } from '@models/shopping.model';
import { TranslationPipe } from '@pipes/translation/translation.pipe';
import { ActivityManagerService } from '@services/activity/manager/activity-manager.service';
import { ShoppingService } from '@services/shopping/shopping.service';
import { ThemeService } from '@services/theme/theme.service';
import { TranslationService } from '@services/translation/translation.service';
import { Observable, Subject, Subscription, takeUntil } from 'rxjs';
import { DataLoaderCaptureOverrideDirective, DataLoaderDirective } from 'tangoe-design-system';

@Component({
  selector: 'ts-price-summary',
  templateUrl: './price-summary.component.html',
  hostDirectives: [DataLoaderCaptureOverrideDirective],
  imports: [CommonModule, DataLoaderDirective, TranslationPipe]
})
export class PriceSummaryComponent implements OnInit, OnDestroy {
  @Input() id: number | string | null;
  @Input() navigate_page: string;
  @Input() hide_price_content: boolean = true;
  @Input() skip_button: boolean = false;
  @Input() button_status: boolean = true;
  @Input() button_label: string = this.translation_svc.translate('continue');
  @Input() hide_one_time_cost: boolean = true;
  @Input() is_display_one_time_cost: boolean = true;
  @Input() is_display_recurring_cost: boolean = true;
  @Input() reset_cart: boolean = false;
  @Input() shopping_cart: ShoppingCartData;
  @Input() shopping_items_price: ShoppingCartData = {
    shopping_cart_items: [],
    orderTypeId: 0,
    lineId: 0
  };
  @Input() in_cart_device: DeviceItemDevice | null;
  @Input() keep_plan: boolean = false;
  @Input() refresh_summary: Observable<boolean | string>;
  @Output() updateCostSummary: EventEmitter<ShoppingCartData> = new EventEmitter();
  @Output() onContinueSuccess: EventEmitter<boolean> = new EventEmitter();
  summary_data: PriceData = {
    hide_price: false,
    one_time_cost: '- -',
    recurring_cost: '- -'
  };
  private price_observer: Subject<void> = new Subject<void>();
  private subscriptions: Subscription = new Subscription();

  constructor(
    private activity_manager_svc: ActivityManagerService,
    private router: Router,
    private shopping_svc: ShoppingService,
    public theme_svc: ThemeService,
    private translation_svc: TranslationService
  ) {}

  ngOnInit(): void {
    if (!this.shopping_items_price.orderTypeId)
      this.shopping_items_price.orderTypeId = this.activity_manager_svc.currentOrderTypeId();

    this.subscriptions.add(
      this.refresh_summary?.subscribe({
        next: (response: string | boolean) => {
          this.price_observer.next();

          if (response === 'refresh_cart_list') this.getCartItems();
          else if (response)
            if (this.shopping_items_price.shopping_cart_items.length === 0) {
              CostInfo.one_time_cost = '- -';
              CostInfo.recurring_cost = '- -';
              this.initialCostLoad();
            } else setTimeout(() => this.getShoppingPrice(), 200);
        },
        error: (err: HttpErrorResponse) => {}
      })
    );

    this.initialCostLoad();
    this.getCartItems();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.price_observer.unsubscribe();
  }

  continue(): void {
    if (
      !this.shopping_cart.authorized_to_place_order &&
      this.router.url.includes('/finalize-order')
    ) {
      this.activity_manager_svc.unauthorizedActivity();
    } else {
      this.addCostToSession();
      this.addShoppingItems();
    }
  }

  private initialCostLoad(): void {
    this.summary_data.one_time_cost = CostInfo.one_time_cost;
    this.summary_data.recurring_cost = CostInfo.recurring_cost;
  }

  private getCartItems(): void {
    this.subscriptions.add(
      this.shopping_svc.getShoppingItems().subscribe({
        next: (res: ShoppingCartData) => {
          this.updateCostSummary.emit(res);

          if (!this.shopping_svc.preselected_item)
            this.shopping_items_price.shopping_cart_items = res.shopping_cart_items.map(
              (item: ShoppingCartItem) => ({
                type: item.type,
                id: item.id,
                quantity: item.quantity,
                sub_type: item.sub_type
              })
            );

          this.shopping_items_price.keep_plan = res.keep_plan ?? undefined;
          this.shopping_items_price.lineId = res.lineId ?? undefined;
          this.shopping_items_price.existing_asset = res.existing_asset ?? undefined;

          this.getShoppingPrice();
        },
        error: (err: HttpErrorResponse) => {}
      })
    );
  }

  private getShoppingPrice(): void {
    this.shopping_svc
      .getShoppingPrice(this.shopping_items_price)
      .pipe(takeUntil(this.price_observer))
      .subscribe({
        next: (res: PriceData) => {
          this.summary_data = res;
          if (
            !res.one_time_cost ||
            !this.is_display_one_time_cost ||
            this.shopping_cart?.shopping_cart_items?.length === 0
          )
            this.summary_data.one_time_cost = '- -';

          if (!res.recurring_cost || !this.is_display_recurring_cost)
            this.summary_data.recurring_cost = '- -';
        },
        error: (err: HttpErrorResponse) => {}
      });
  }

  private addCostToSession(): void {
    CostInfo.one_time_cost = this.summary_data.one_time_cost;
    CostInfo.recurring_cost = this.summary_data.recurring_cost;
  }

  private addShoppingItems(): void {
    if (this.activity_manager_svc.state().getOrderForPerson())
      this.shopping_cart.order_for_person = this.activity_manager_svc.state().getOrderForPerson();

    this.shopping_cart.shopping_cart_items =
      [3].includes(this.activity_manager_svc.currentOrderTypeId()) || this.in_cart_device
        ? this.shopping_cart.shopping_cart_items.filter(
            (item: ShoppingCartItem) => item.type !== CART_ITEM_TYPE_DEVICE
          )
        : this.shopping_cart.shopping_cart_items;

    this.shopping_cart.progress_stepper = this.activity_manager_svc
      .state()
      .currentStep(this.activity_manager_svc.state().activeStepIndex());

    this.subscriptions.add(
      this.shopping_svc.sendShoppingItems(this.shopping_cart, this.reset_cart).subscribe({
        next: (res: ShoppingCartData) => {
          this.activity_manager_svc
            .state()
            .setParameter('device_include_admin_options', res.device_include_admin_options);
          this.activity_manager_svc
            .state()
            .setParameter(
              'accessories_include_admin_options',
              res.accessories_include_admin_options
            );

          this.onContinueSuccess.emit(true);
          if (this.navigate_page) this.navigateToNextPage();
        },
        error: (err: HttpErrorResponse) => {}
      })
    );
  }

  private navigateToNextPage(): void {
    this.router.navigate([this.navigate_page], {
      queryParams: {
        deviceId: this.id,
        keep_plan: this.keep_plan
      }
    });
  }
}
